import { create } from 'zustand';
import { snapshotService } from '@/services/snapshotService';
import { toast } from 'sonner';

interface Snapshot {
  id: string;
  name: string;
  description: string | null;
  snapshot_type: string;
  created_at: string;
  created_by: string | null;
  record_counts: any;
  size_bytes: number | null;
}

interface SnapshotStore {
  snapshots: Snapshot[];
  isLoading: boolean;
  lastAutoSave: Date | null;
  autoSaveEnabled: boolean;
  storageUsed: number;
  fileStorageUsed: number;
  snapshotStorageUsed: number;
  storageLimit: number;
  storagePercentage: number;
  
  loadSnapshots: () => Promise<void>;
  loadStorageStats: () => Promise<void>;
  createSnapshot: (name: string, description?: string, type?: 'manual' | 'auto' | 'pre-restore' | 'pre-import') => Promise<void>;
  restoreSnapshot: (snapshotId: string) => Promise<void>;
  deleteSnapshot: (snapshotId: string) => Promise<void>;
  startAutoSave: () => void;
  stopAutoSave: () => void;
  performAutoSave: () => Promise<void>;
}

let autoSaveInterval: NodeJS.Timeout | null = null;

export const useSnapshotStore = create<SnapshotStore>((set, get) => ({
  snapshots: [],
  isLoading: false,
  lastAutoSave: null,
  autoSaveEnabled: true,
  storageUsed: 0,
  fileStorageUsed: 0,
  snapshotStorageUsed: 0,
  storageLimit: 209715200, // 200MB
  storagePercentage: 0,

  loadSnapshots: async () => {
    set({ isLoading: true });
    try {
      const snapshots = await snapshotService.listSnapshots();
      set({ snapshots, isLoading: false });
      await get().loadStorageStats();
    } catch (error) {
      console.error('Error loading snapshots:', error);
      toast.error('Failed to load snapshots');
      set({ isLoading: false });
    }
  },

  loadStorageStats: async () => {
    try {
      const stats = await snapshotService.getStorageStats();
      set({ 
        storageUsed: stats.used,
        fileStorageUsed: stats.fileStorageUsed,
        snapshotStorageUsed: stats.snapshotStorageUsed,
        storageLimit: stats.limit,
        storagePercentage: stats.percentage,
      });
    } catch (error) {
      console.error('Error loading storage stats:', error);
    }
  },

  createSnapshot: async (name, description = '', type = 'manual') => {
    set({ isLoading: true });
    try {
      await snapshotService.createSnapshot(name, description, type);
      toast.success('Snapshot created successfully');
      
      // Cleanup old auto-saves if this was an auto-save
      if (type === 'auto') {
        await snapshotService.cleanupAutoSaves(7);
      }
      
      await get().loadSnapshots();
      set({ isLoading: false, lastAutoSave: new Date() });
    } catch (error) {
      console.error('Error creating snapshot:', error);
      toast.error('Failed to create snapshot');
      set({ isLoading: false });
      throw error;
    }
  },

  restoreSnapshot: async (snapshotId) => {
    set({ isLoading: true });
    try {
      // Create a pre-restore snapshot first
      await snapshotService.createSnapshot(
        `Before restore - ${new Date().toLocaleString()}`,
        'Automatic backup before restore',
        'pre-restore'
      );

      await snapshotService.restoreSnapshot(snapshotId);
      toast.success('Snapshot restored successfully');
      await get().loadSnapshots();
      set({ isLoading: false });
      
      // Reload the page to refresh all data
      window.location.reload();
    } catch (error) {
      console.error('Error restoring snapshot:', error);
      toast.error('Failed to restore snapshot');
      set({ isLoading: false });
      throw error;
    }
  },

  deleteSnapshot: async (snapshotId) => {
    set({ isLoading: true });
    try {
      await snapshotService.deleteSnapshot(snapshotId);
      toast.success('Snapshot deleted');
      await get().loadSnapshots();
      set({ isLoading: false });
    } catch (error) {
      console.error('Error deleting snapshot:', error);
      toast.error('Failed to delete snapshot');
      set({ isLoading: false });
    }
  },

  performAutoSave: async () => {
    const { autoSaveEnabled, isLoading } = get();
    if (!autoSaveEnabled || isLoading) return;

    try {
      const timestamp = new Date().toLocaleString();
      await snapshotService.createSnapshot(
        `Auto-save ${timestamp}`,
        'Automatic daily backup',
        'auto'
      );
      await snapshotService.cleanupAutoSaves(7);
      set({ lastAutoSave: new Date() });
    } catch (error) {
      console.error('Auto-save failed:', error);
    }
  },

  startAutoSave: () => {
    if (autoSaveInterval) return;

    // Run auto-save every 24 hours
    autoSaveInterval = setInterval(() => {
      get().performAutoSave();
    }, 24 * 60 * 60 * 1000);

    set({ autoSaveEnabled: true });
  },

  stopAutoSave: () => {
    if (autoSaveInterval) {
      clearInterval(autoSaveInterval);
      autoSaveInterval = null;
    }
    set({ autoSaveEnabled: false });
  },
}));
